import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../../contexts/PusherContext';
import api from '../../../api';
import {
  AgentEvents,
  BookingSection,
  BookingSectionConfig,
  BookingSectionsUpdatedEvent,
  BookingOption,
  BookingOptionCreatedEvent,
  BookingOptionUpdatedEvent,
} from '@dreamai/types';
import { ReactComponent as CaretExpanded } from '../../../icons/caret-expanded.svg';
import AgentStatusPill from './AgentStatusPill';
import BookingSectionGrid, {
  getColumns,
  getOptionTitle,
  BookingSectionGridHandle,
} from './BookingSectionGrid';
import BookingSectionMapCards from './BookingSectionMapCards';
import { useReplyToActions } from '../../../contexts/ReplyToContext';
import ProjectAppResearchSection from './ProjectAppResearchSection';
import './booking-sections.scss';

interface BookingSectionsProps {
  threadId: string | null;
}

const BookingSections: React.FC<BookingSectionsProps> = ({ threadId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [sections, setSections] = useState<BookingSection[]>([]);
  const [config, setConfig] = useState<BookingSectionConfig | undefined>();
  const [options, setOptions] = useState<Record<string, BookingOption[]>>({});
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSections = useCallback(async () => {
    if (!threadId) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get(`/booking-sections/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && res.data.sections) {
        const fetched: BookingSection[] = res.data.sections;
        setSections(fetched);
        setConfig(res.data.config);
        setOptions(res.data.options || {});
        // Expand the first section by default
        setExpandedSection(fetched.length > 0 ? fetched[0].slug : null);
      }
    } catch (err) {
      console.error('[BookingSections] Error fetching sections:', err);
    } finally {
      setLoading(false);
    }
  }, [threadId, getAccessTokenSilently]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  // Listen for real-time section updates via Pusher
  useEffect(() => {
    if (!pusherChannel || !threadId) return;
    const handler = (event: BookingSectionsUpdatedEvent) => {
      if (event.threadId !== threadId) return;
      setSections(event.sections);
      if (event.config) setConfig(event.config);
      // If a new section appeared, expand it
      setExpandedSection((prev) => {
        const prevSlugs = sections.map((s) => s.slug);
        const newSection = event.sections.find(
          (s) => !prevSlugs.includes(s.slug),
        );
        return newSection ? newSection.slug : prev;
      });
    };
    pusherChannel.bind(AgentEvents.BOOKING_SECTIONS_UPDATED, handler);
    return () => {
      pusherChannel.unbind(AgentEvents.BOOKING_SECTIONS_UPDATED, handler);
    };
  }, [pusherChannel, threadId]);

  // Listen for booking option created/updated via Pusher
  useEffect(() => {
    if (!pusherChannel || !threadId) return;

    const onCreated = (event: BookingOptionCreatedEvent) => {
      if (event.threadId !== threadId) return;
      setOptions((prev) => {
        const slug = event.sectionSlug;
        const existing = prev[slug] || [];
        return { ...prev, [slug]: [...existing, event.option] };
      });
    };

    const onUpdated = (event: BookingOptionUpdatedEvent) => {
      if (event.threadId !== threadId) return;
      setOptions((prev) => {
        const slug = event.sectionSlug;
        const existing = prev[slug] || [];
        return {
          ...prev,
          [slug]: existing.map((o) =>
            o.id === event.option.id ? event.option : o,
          ),
        };
      });
    };

    pusherChannel.bind(AgentEvents.BOOKING_OPTION_CREATED, onCreated);
    pusherChannel.bind(AgentEvents.BOOKING_OPTION_UPDATED, onUpdated);
    return () => {
      pusherChannel.unbind(AgentEvents.BOOKING_OPTION_CREATED, onCreated);
      pusherChannel.unbind(AgentEvents.BOOKING_OPTION_UPDATED, onUpdated);
    };
  }, [pusherChannel, threadId]);

  const {
    addReplyTo,
    removeReplyTo,
    registerRemoveCallback,
    unregisterRemoveCallback,
  } = useReplyToActions();

  // Refs to grid instances keyed by section slug
  const gridRefsMap = useRef<Record<string, BookingSectionGridHandle | null>>(
    {},
  );

  // Register callback so removing a replyTo item deselects it in the grid
  useEffect(() => {
    const key = 'BookingSections';
    registerRemoveCallback(key, (id: string) => {
      if (id === 'flights') {
        // Find the return_flights grid and clear its return selection
        for (const section of sections) {
          if (section.view === 'return_flights') {
            gridRefsMap.current[section.slug]?.clearReturnSelection();
            break;
          }
        }
        return;
      }
      // booking:{slug}:{optId}
      if (id.startsWith('booking:')) {
        const parts = id.split(':');
        const slug = parts[1];
        const optId = parts.slice(2).join(':');
        gridRefsMap.current[slug]?.deselectOption(optId);
      }
    });
    return () => unregisterRemoveCallback(key);
  }, [sections, registerRemoveCallback, unregisterRemoveCallback]);

  const handleSectionSelectionChange = useCallback(
    (slug: string, ids: Set<string>) => {
      const section = sections.find((sec) => sec.slug === slug);
      const sectionOpts = options[slug] || [];
      const cols = getColumns(section!, sectionOpts);

      // Remove items for options that were deselected
      for (const opt of sectionOpts) {
        if (!ids.has(opt.id)) {
          removeReplyTo(`booking:${slug}:${opt.id}`);
        }
      }

      // Add/update items for selected options
      ids.forEach((optId) => {
        const opt = sectionOpts.find((o) => o.id === optId);
        if (!opt) return;
        const title = getOptionTitle(opt, section!, cols);
        const d = opt.data as Record<string, any> | undefined;
        addReplyTo({
          id: `booking:${slug}:${optId}`,
          actionLine: section?.label || slug,
          descriptionLine: title,
          extraContext: `${section?.label || slug}: ${optId}`,
          bookable: true,
          price: d?.price != null ? Number(d.price) : undefined,
          price_currency: d?.price_currency || undefined,
        });
      });
    },
    [sections, options, addReplyTo, removeReplyTo],
  );

  const handleReturnFlightSelected = useCallback(
    (
      outboundOption: BookingOption | null,
      returnFlight: Record<string, any> | null,
    ) => {
      if (!outboundOption || !returnFlight) {
        removeReplyTo('flights');
        return;
      }
      const d = outboundOption.data as Record<string, any> | undefined;
      const fmtTime = (v: any) => {
        if (!v) return '??:??';
        if (typeof v === 'string' && /T\d{2}:\d{2}/.test(v)) {
          return v.slice(v.indexOf('T') + 1, v.indexOf('T') + 6);
        }
        return String(v);
      };
      const outDesc = d
        ? `${fmtTime(d.departure_time)} ${d.departure_airport || ''} \u2192 ${fmtTime(d.arrival_time)} ${d.arrival_airport || ''} (${d.airlines || ''})`
        : outboundOption.id;
      const retDesc = `${fmtTime(returnFlight.departure_time)} ${returnFlight.departure_airport || ''} \u2192 ${fmtTime(returnFlight.arrival_time)} ${returnFlight.arrival_airport || ''} (${returnFlight.airlines || ''})`;

      const totalPrice =
        returnFlight.price != null ? Number(returnFlight.price) : undefined;
      const currency = returnFlight.price_currency || d?.price_currency;

      addReplyTo({
        id: 'flights',
        actionLine: 'Selected flights',
        descriptionLine: `Out: ${outDesc}. Return: ${retDesc}`,
        extraContext: JSON.stringify({
          outbound: { booking_option_id: outboundOption.id, ...d },
          return_flight: returnFlight,
        }),
        bookable: true,
        price: totalPrice || undefined,
        price_currency: currency || undefined,
      });
    },
    [addReplyTo, removeReplyTo],
  );

  const toggleSection = useCallback((slug: string) => {
    setExpandedSection((prev) => (prev === slug ? null : slug));
  }, []);

  if (loading) return null;
  if (sections.length === 0) return null;

  return (
    <div className='booking-sections'>
      {(config?.sectionsTitle || config?.subHeadline) && (
        <div className='booking-sections-header'>
          <div className='titles-section'>
            {config.sectionsTitle && (
              <div className='booking-sections-title'>
                {config.sectionsTitle}
              </div>
            )}
            {config.subHeadline && (
              <div className='booking-sections-subtitle'>
                {config.subHeadline}
              </div>
            )}
          </div>
          <div className='agent-todos-area'>
            {threadId && <AgentStatusPill threadId={threadId} />}
          </div>
        </div>
      )}

      {threadId && <ProjectAppResearchSection threadId={threadId} />}
      <hr />

      {sections.map((section) => {
        const isExpanded = expandedSection === section.slug;
        const sectionOptions = options[section.slug] || [];
        const columns = getColumns(section, sectionOptions);
        return (
          <div key={section.slug} className='booking-section'>
            <div
              className='booking-section-header'
              onClick={() => toggleSection(section.slug)}
            >
              <div
                className={`booking-section-caret${isExpanded ? ' expanded' : ''}`}
              >
                <CaretExpanded />
              </div>
              <span className='booking-section-label'>{section.label}</span>
            </div>
            <div
              className={`booking-section-collapse${isExpanded ? ' expanded' : ''}`}
            >
              <div className='booking-section-content'>
                {sectionOptions.length === 0 &&
                !(
                  (section.view === 'hotels' ||
                    section.view === 'card_with_maps') &&
                  (section.pointsOfInterest?.length ?? 0) > 0
                ) ? (
                  <div className='booking-section-placeholder'>
                    No options yet
                  </div>
                ) : section.view === 'hotels' ||
                  section.view === 'card_with_maps' ? (
                  <BookingSectionMapCards
                    section={section}
                    sectionOptions={sectionOptions}
                    columns={columns}
                    onSelectionChange={handleSectionSelectionChange}
                  />
                ) : (
                  <div className='booking-options-table-wrapper'>
                    <BookingSectionGrid
                      ref={(handle) => {
                        gridRefsMap.current[section.slug] = handle;
                      }}
                      section={section}
                      sectionOptions={sectionOptions}
                      columns={columns}
                      onSelectionChange={handleSectionSelectionChange}
                      onReturnFlightSelected={handleReturnFlightSelected}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BookingSections;
